import { Trash2 } from "lucide-react";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { Select } from "@/shared/components/shadcn/Select";
import { TextArea } from "@/shared/components/shadcn/TextArea";
import { FormField } from "@/shared/components/ui/FormField";
import type { BuilderDraft, BuilderField, SectionElement } from "@froppio/form-core";
import { findContainer, findElement } from "../utils/builderOperations";
import { isChoiceWidget, widgetLabel } from "@froppio/form-core";
import { BuilderChoiceSettings } from "./BuilderChoiceSettings";

interface BuilderSettingsProps {
  draft: BuilderDraft;
  selectedId: string | null;
  onFieldChange: (field: BuilderField) => void;
  onSectionChange: (section: SectionElement) => void;
  onChangeContainer: (id: string, sectionId: string | null) => void;
  onDelete: (id: string) => void;
}

export function BuilderSettings({ draft, selectedId, onFieldChange, onSectionChange, onChangeContainer, onDelete }: BuilderSettingsProps) {
  const selected = selectedId ? findElement(draft, selectedId) : undefined;
  const field = selected?.type === "field" ? draft.fields[selected.fieldId] : undefined;
  return (
    <aside aria-label="Item settings" className="min-w-0 space-y-5 rounded-[20px] border border-border bg-card p-4">
      <h2 className="text-base font-bold">Settings</h2>
      {!selected && <p className="text-sm text-muted-foreground">Select a question or section on the canvas.</p>}
      {selected?.type === "section" && (
        <>
          <FormField id="section-title" label="Section title">
            <Input value={selected.title} onChange={event => onSectionChange({ ...selected, title: event.target.value })} />
          </FormField>
          <FormField id="section-description" label="Description" optional>
            <TextArea rows={4} value={selected.description} onChange={event => onSectionChange({ ...selected, description: event.target.value })} />
          </FormField>
          <p className="text-xs text-muted-foreground">Moving this section moves all its questions. Sections cannot be nested.</p>
        </>
      )}
      {field && (
        <>
          <p className="text-xs text-muted-foreground">{widgetLabel(field.widget)}</p>
          <FormField id="question-label" label="Question label">
            <Input value={field.label} onChange={event => onFieldChange({ ...field, label: event.target.value })} />
          </FormField>
          <FormField id="question-help" label="Help text" optional>
            <TextArea rows={3} value={field.helpText} onChange={event => onFieldChange({ ...field, helpText: event.target.value })} />
          </FormField>
          <label className="flex min-h-11 items-center gap-3 text-sm font-semibold">
            <input type="checkbox" checked={field.required} onChange={event => onFieldChange({ ...field, required: event.target.checked })}
              className="size-5 rounded border-input accent-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
            Required answer
          </label>
          {isChoiceWidget(field.widget) && <BuilderChoiceSettings field={field} onChange={onFieldChange} />}
          {(field.widget === "radio" || field.widget === "dropdown") && (
            <FormField id="choice-presentation" label="Display as">
              <Select value={field.widget} onChange={event => onFieldChange({ ...field, widget: event.target.value === "radio" ? "radio" : "dropdown" })}>
                <option value="radio">Radio buttons</option>
                <option value="dropdown">Dropdown</option>
              </Select>
            </FormField>
          )}
          <FormField id="question-section" label="Section" description="Moving to another section places the question at its end.">
            <Select value={findContainer(draft, field.id)?.id ?? ""} onChange={event => onChangeContainer(field.id, event.target.value || null)}>
              <option value="">Outside sections</option>
              {draft.elements.filter(element => element.type === "section").map(section => (
                <option key={section.id} value={section.id}>{section.title || "Untitled section"}</option>
              ))}
            </Select>
          </FormField>
        </>
      )}
      {selected && <Button variant="outline" className="w-full text-destructive" onClick={() => onDelete(selectedId!)}>
        <Trash2 aria-hidden="true" />Delete {selected.type === "section" ? "section" : "question"}
      </Button>}
    </aside>
  );
}
